/**
 * DiscoveryDebugPanel — dev/test harness for the JVVNL tips system.
 *
 * Renders a small floating control panel (bottom-LEFT, opposite the tip card) to
 * manually drive and inspect the discovery engine without waiting the 30–60s load
 * delay or replaying real behavior:
 *
 *   • One-click "trigger" button per tip and per guard — forces that exact
 *     notification to render, bypassing page/context/score/trigger
 *   • Force-evaluate now (bypasses load wait + frequency cooldown)
 *   • Set session count → flip new/returning/power profile + stage gate
 *   • Publish AppState fields (page, selectedEntityCount, currentView,
 *     appliedFilterCount) so contextCheck-gated tips become eligible naturally
 *   • Clear all persisted tip flags / suppressed categories / guard flags
 *   • Live view of the active tip + active guard + current published state
 *
 * Gated behind a query flag so it never ships to real users:
 *   add `?tipdebug=1` to the URL  (or set localStorage.jvvnl_tipdebug = "1")
 *
 * Render it inside <DiscoveryProvider>, next to <FloatingTipCard />.
 */

import React, { useState } from "react";

import { useDiscovery } from "./DiscoveryContext";
import { TIP_DATABASE } from "./tipDatabase";
import { GUARDS } from "./workflowRules";
import { KEYS, getSessionCount, writeSessionCount } from "./storage";

function debugEnabled(): boolean {
  try {
    const q = new URLSearchParams(window.location.search);
    if (q.get("tipdebug") === "1") return true;
    return localStorage.getItem("jvvnl_tipdebug") === "1";
  } catch {
    return false;
  }
}

const box: React.CSSProperties = {
  position: "fixed",
  bottom: 16,
  left: 16,
  zIndex: 99999,
  width: 300,
  maxHeight: "80vh",
  overflowY: "auto",
  background: "rgba(17,19,26,.94)",
  color: "#e8eaf0",
  border: "1px solid rgba(255,255,255,.12)",
  borderRadius: 10,
  padding: 12,
  font: "12px/1.4 system-ui, sans-serif",
  boxShadow: "0 8px 32px rgba(0,0,0,.5)",
  backdropFilter: "blur(8px)",
};

const btn: React.CSSProperties = {
  background: "#2b6cff",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  padding: "5px 8px",
  cursor: "pointer",
  fontSize: 11,
};

const ghost: React.CSSProperties = {
  ...btn,
  background: "rgba(255,255,255,.08)",
};

const input: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  background: "rgba(255,255,255,.06)",
  border: "1px solid rgba(255,255,255,.14)",
  borderRadius: 6,
  color: "#e8eaf0",
  padding: "4px 6px",
  fontSize: 11,
  marginTop: 2,
};

const label: React.CSSProperties = { color: "#9aa3b2", fontSize: 10, marginTop: 8, display: "block" };

export const DiscoveryDebugPanel: React.FC = () => {
  const {
    activeTip,
    activeTipText,
    appState,
    setAppContext,
    forceEvaluate,
    dismissTip,
    activeGuard,
    dismissGuard,
    forceShowTip,
    forceShowGuard,
  } = useDiscovery();

  const [open, setOpen] = useState(true);
  const [sessionCount, setSessionCount] = useState(() => getSessionCount());
  const [selectedEntityCount, setSelected] = useState(0);
  const [appliedFilterCount, setFilters] = useState(0);
  const [currentView, setView] = useState("chart");

  if (!debugEnabled()) return null;

  const applySession = () => {
    writeSessionCount(sessionCount);
  };

  const clearFlags = () => {
    try {
      localStorage.removeItem(KEYS.tipState);
      localStorage.removeItem(KEYS.suppressedCategories);
      localStorage.removeItem(KEYS.guardState);
    } catch {
      /* ignore */
    }
  };

  const publish = () => {
    setAppContext({ selectedEntityCount, appliedFilterCount, currentView });
  };

  const fireAll = () => {
    applySession();
    publish();
    // allow the published state to flush before evaluating
    setTimeout(() => forceEvaluate(), 50);
  };

  if (!open) {
    return (
      <button style={{ ...box, width: "auto", padding: "6px 10px" }} onClick={() => setOpen(true)}>
        🧪 tips
      </button>
    );
  }

  return (
    <div style={box}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <strong>🧪 Tip Debug</strong>
        <button style={ghost} onClick={() => setOpen(false)}>
          –
        </button>
      </div>

      <span style={label}>Active tip</span>
      <div style={{ background: "rgba(255,255,255,.05)", borderRadius: 6, padding: 6 }}>
        {activeTip ? (
          <>
            <div style={{ fontWeight: 600 }}>
              {activeTip.id} <span style={{ color: "#9aa3b2" }}>({activeTip.category})</span>
            </div>
            <div style={{ color: "#c4cad6" }}>{activeTipText}</div>
            <button style={{ ...ghost, marginTop: 4 }} onClick={dismissTip}>
              dismiss
            </button>
          </>
        ) : (
          <em style={{ color: "#9aa3b2" }}>none</em>
        )}
      </div>

      <span style={label}>Published state — page: {appState.page || "(none)"}</span>

      <span style={label}>session_count (profile + stage gate)</span>
      <input
        style={input}
        type="number"
        value={sessionCount}
        onChange={(e) => setSessionCount(Number(e.target.value))}
      />
      <small style={{ color: "#9aa3b2" }}>
        ≤3 new (basic only) · 4–15 returning · 16+ power
      </small>

      <span style={label}>selectedEntityCount (cumulative-mode ≥2)</span>
      <input
        style={input}
        type="number"
        value={selectedEntityCount}
        onChange={(e) => setSelected(Number(e.target.value))}
      />

      <span style={label}>appliedFilterCount (deep-link ≥2)</span>
      <input
        style={input}
        type="number"
        value={appliedFilterCount}
        onChange={(e) => setFilters(Number(e.target.value))}
      />

      <span style={label}>currentView (csv-export = "table")</span>
      <input style={input} value={currentView} onChange={(e) => setView(e.target.value)} />

      <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
        <button style={btn} onClick={fireAll}>
          ⚡ Apply + Force eval
        </button>
        <button style={ghost} onClick={publish}>
          publish state
        </button>
        <button style={ghost} onClick={() => forceEvaluate()}>
          force eval
        </button>
        <button style={ghost} onClick={clearFlags}>
          clear flags
        </button>
      </div>

      <div
        style={{
          borderTop: "1px solid rgba(255,255,255,.12)",
          marginTop: 12,
          paddingTop: 8,
        }}
      >
        <strong style={{ color: "#f1935c" }}>⚠ Workflow Guards</strong>

        <span style={label}>Active guard</span>
        <div style={{ background: "rgba(255,255,255,.05)", borderRadius: 6, padding: 6 }}>
          {activeGuard ? (
            <>
              <div style={{ fontWeight: 600 }}>
                {activeGuard.guard.id}{" "}
                <span style={{ color: "#9aa3b2" }}>({activeGuard.guard.category})</span>
              </div>
              <div style={{ color: "#c4cad6" }}>{activeGuard.text}</div>
              <button style={{ ...ghost, marginTop: 4 }} onClick={dismissGuard}>
                dismiss
              </button>
            </>
          ) : (
            <em style={{ color: "#9aa3b2" }}>none</em>
          )}
        </div>

        <span style={label}>Trigger a guard (forces it to show)</span>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {GUARDS.map((g) => (
            <button
              key={g.id}
              style={{ ...ghost, textAlign: "left" }}
              onClick={() => forceShowGuard(g.id)}
            >
              ⚠ {g.id}
            </button>
          ))}
        </div>
      </div>

      <div
        style={{
          borderTop: "1px solid rgba(255,255,255,.12)",
          marginTop: 12,
          paddingTop: 8,
        }}
      >
        <strong style={{ color: "#f1935c" }}>⚡ Trigger a tip ({TIP_DATABASE.length})</strong>
        <span style={label}>forces it to show, bypassing page/context/score</span>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {TIP_DATABASE.map((t) => (
            <button
              key={t.id}
              style={{ ...ghost, textAlign: "left" }}
              onClick={() => forceShowTip(t.id)}
            >
              ⚡ {t.id}{" "}
              <span style={{ color: "#9aa3b2" }}>({t.category})</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
